import { isDefined } from '@sgme/fp';
import type { IScheduledQuotesBlotterItem } from 'apiConnector/scheduledRun/scheduledQuotesBlotter';
import type { Run } from 'apiConnector/scheduledRun/scheduledRuns';
import type { ColumnGroupPreferenceState, ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { SchedulePanelTab } from 'state/blotters/scheduledQuotes';
import {
  EDITABLE_SCHEDULED_RUN_BLOTTER_ID,
  getEditableScheduledRunId,
  getSelectedItem,
} from 'state/blotters/scheduledQuotes';
import {
  scheduledQuotesBlotterColumnStateChanged,
  scheduledQuotesBlotterItemReceived,
  scheduledQuotesBlotterItemsReceived,
  scheduledQuotesBlotterPanelClosed,
  scheduledQuotesBlotterPanelTabChanged,
  scheduledQuotesBlotterPreferenceStatePersisted,
  scheduledQuotesBlotterSelectionChanged,
} from 'state/blotters/scheduledQuotes/scheduledQuotesBlotter.action';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import type { SelectableRun } from 'state/scheduledRuns/state';
import { defaultFrequency, initScheduleState, type ScheduledRun } from 'state/scheduledRuns/state';
import type { Thunk } from 'state/state.common';
import { isSuccess } from 'utils/status';
export function scheduledQuotesBlotterPanelClosedThunk(): Thunk<void> {
  return dispatch => {
    dispatch(scheduledQuotesBlotterSelectionChanged(undefined));
    dispatch(scheduledQuotesBlotterPanelClosed());
  };
}

export function scheduledQuotesBlotterColumnStateChangedThunk(
  columnState: readonly ColumnPreferenceState[],
  columnGroupState?: readonly ColumnGroupPreferenceState[],
): Thunk<void> {
  return (
    dispatch,
    getState,
    {
      selectors: {
        scheduledQuotesBlotter: { getBlotterPreferenceState },
      },
    },
  ) => {
    const state = getState();
    const blotterPreferenceState = getBlotterPreferenceState(state);

    dispatch(scheduledQuotesBlotterColumnStateChanged(columnState, columnGroupState));
    dispatch(
      scheduledQuotesBlotterPreferenceStatePersisted({ ...blotterPreferenceState, columnState, columnGroupState }),
    );
  };
}

export function scheduledQuotesBlotterSelectionChangedThunk(selectedRowId: string | undefined): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    if (selectedRowId) {
      const run = state.blotters.scheduledQuotes.items.find(run => run.id === selectedRowId);
      if (run) {
        dispatch(initRunThunk(run));
      }
    }
    dispatch(scheduledQuotesBlotterSelectionChanged(selectedRowId));
  };
}

function mapSelectableRunToScheduleRun(run: SelectableRun): ScheduledRun {
  return {
    ...initScheduleState(),
    days: run.daysTrigger ? [...run.daysTrigger.days] : [],
    comment: run.comment,
    frequency: run.rangeTrigger?.frequency ?? defaultFrequency(),
    quotes: [...run.quotes],
    triggerMode: run.rangeTrigger ? 'range' : 'days',
    readers: [...run.readers],
    time: run.daysTrigger?.time ?? run.rangeTrigger?.time,
    runs: {
      content: [{ isSelected: true, ...run }],
      status: 'success',
    },
    range: run.rangeTrigger ? { startDate: run.rangeTrigger.startDate, endDate: run.rangeTrigger.endDate } : undefined,
    title: run.name,
  };
}
export function sendResetRunThunk(): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const runId = getEditableScheduledRunId(state);
    if (!runId) {
      return;
    }

    dispatch(scheduledRunActions.resetRunRequested(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, runId));
  };
}
export function resetRunThunk(): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const run = state.scheduledRun.runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID];
    if (isSuccess(run?.runs)) {
      dispatch(
        scheduledRunActions.init(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, mapSelectableRunToScheduleRun(run.runs.content[0])),
      );
    }
  };
}
export function setRunThunk(run: Run): Thunk<void> {
  return dispatch => {
    dispatch(scheduledQuotesBlotterItemReceived(mapRunToBlotterItem(run)));
    dispatch(scheduledRunActions.init(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, mapSelectableRunToScheduleRun(run)));
  };
}

function mapRunToBlotterItem({
  etag,
  id,
  quotes,
  readers,
  comment,
  daysTrigger,
  name,
  rangeTrigger,
  requester,
  status,
  lastRunTime,
  nextRunTime,
}: Run): IScheduledQuotesBlotterItem {
  return {
    etag,
    name,
    requester,
    type: isDefined(rangeTrigger) ? 'range' : 'days',
    id,
    quotes,
    readers: [...readers],
    triggerTime: (rangeTrigger?.time ?? daysTrigger?.time)!,
    timeZone: (rangeTrigger?.timeZone ?? daysTrigger?.timeZone)!,
    status,
    comment,
    frequency: rangeTrigger?.frequency,
    lastRunTime,
    nextRunTime,
    scheduledRangeOrDays: {
      triggerDays: daysTrigger?.days ?? [],
      startDate: rangeTrigger?.startDate,
      endDate: rangeTrigger?.endDate,
    },
  };
}
function mapBlotterItemToRun({ scheduledRangeOrDays, ...run }: IScheduledQuotesBlotterItem): SelectableRun {
  return {
    etag: run.etag,
    name: run.name,
    requester: run.requester,
    id: run.id,
    isSelected: true,
    quotes: run.quotes,
    readers: [...run.readers],
    status: run.status,
    comment: run.comment,
    daysTrigger: { days: scheduledRangeOrDays.triggerDays, time: run.triggerTime, timeZone: run.timeZone },
    rangeTrigger:
      isDefined(scheduledRangeOrDays.startDate) && isDefined(scheduledRangeOrDays.endDate)
        ? {
            startDate: scheduledRangeOrDays.startDate,
            endDate: scheduledRangeOrDays.endDate,
            frequency: run.frequency!,
            time: run.triggerTime,
            timeZone: run.timeZone,
          }
        : undefined,
  };
}

function initRunThunk(run: IScheduledQuotesBlotterItem): Thunk<void> {
  return dispatch => {
    if (run) {
      const formattedRun: ScheduledRun = {
        ...initScheduleState(),
        days: [...run.scheduledRangeOrDays.triggerDays],
        frequency: run.frequency ?? defaultFrequency(),
        comment: run.comment,
        quotes: [...run.quotes],
        triggerMode: run.type,
        readers: [...run.readers],
        time: run.triggerTime,
        runs: {
          content: [mapBlotterItemToRun(run)],
          status: 'success',
        },
        range:
          run.type === 'range'
            ? { startDate: run.scheduledRangeOrDays.startDate!, endDate: run.scheduledRangeOrDays.endDate! }
            : undefined,
        title: run.name,
      };
      dispatch(scheduledRunActions.init(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, formattedRun));
    }
  };
}

export function scheduledQuotesBlotterPanelTabChangedThunk(tab: SchedulePanelTab): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(scheduledQuotesBlotterPanelTabChanged(tab));
    const run = getSelectedItem(state);
    if (run) {
      dispatch(initRunThunk(run));
    }
  };
}

export function scheduledQuotesBlotterItemsReceivedThunk(
  items: readonly IScheduledQuotesBlotterItem[],
  totalItems: number,
  totalPages: number,
): Thunk<void> {
  return (dispatch, getState) => {
    const {
      scheduledRun: { runs },
      blotters: { scheduledQuotes },
    } = getState();
    if (
      scheduledQuotes.selectedRowId &&
      runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID].type === undefined &&
      runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID].confirmBanner === undefined
    ) {
      const updatedRun = items.find(run => run.id === scheduledQuotes.selectedRowId);
      if (updatedRun) {
        dispatch(initRunThunk(updatedRun));
      }
    }
    dispatch(scheduledQuotesBlotterItemsReceived(items, totalItems, totalPages));
  };
}
